import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { selectionService, Selection } from '../../../services/selection';
import { Loader2, Eye, Calendar, User, Package, Share2, X, CheckCircle } from 'lucide-react';
import { format } from 'date-fns-jalali';
import { toast } from 'react-hot-toast';
import Modal from '../../../components/common/Modal';
import { Button } from '../../../components/common/Button';

export const SelectionsManagementPage: React.FC = () => {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [selectedSelection, setSelectedSelection] = useState<Selection | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sharingId, setSharingId] = useState<string | null>(null);

  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['admin-selections', page, statusFilter],
    queryFn: () => selectionService.getAll({ page, page_size: 10, status: statusFilter }),
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) => 
      selectionService.updateStatus(id, status),
    onSuccess: () => {
      toast.success('وضعیت با موفقیت تغییر کرد');
      queryClient.invalidateQueries({ queryKey: ['admin-selections'] });
      setIsModalOpen(false);
    },
    onError: () => {
      toast.error('خطا در تغییر وضعیت');
    }
  });

  const handleOpenModal = async (id: string) => {
    try {
      // Show loading or optimistic? 
      // Better to fetch details to ensure we have products
      const response = await selectionService.getById(id);
      setSelectedSelection(response.data.data);
      setIsModalOpen(true);
    } catch (error) {
      toast.error('خطا در دریافت جزئیات');
    }
  };

  const handleShare = async (selection: Selection) => {
    setSharingId(selection.id);
    try {
      const response = await selectionService.generateShareImage(selection.id);
      const imageUrl = response.image_url;

      if (navigator.share) {
        try {
            const imageBlob = await fetch(imageUrl).then(r => r.blob());
            const file = new File([imageBlob], 'selection.png', { type: 'image/png' });

            if (navigator.canShare && navigator.canShare({ files: [file] })) {
                await navigator.share({
                    files: [file],
                    title: 'لیست منتخب',
                    text: `لیست منتخب کاربر ${selection.user?.first_name || ''} ${selection.user?.last_name || ''}`,
                });
            } else {
                 await navigator.share({
                    title: 'لیست منتخب',
                    text: `لیست منتخب کاربر ${selection.user?.first_name || ''} ${selection.user?.last_name || ''}`,
                    url: imageUrl
                });
            }
        } catch (shareError) {
             console.error('Error sharing:', shareError);
        }
      } else {
         window.open(imageUrl, '_blank');
      }
    } catch (error) {
      console.error('Error generating share image:', error);
      toast.error('خطا در ایجاد تصویر اشتراک‌گذاری');
    } finally {
      setSharingId(null);
    }
  };

  const handleStatusChange = (status: string) => {
    if (selectedSelection) {
        updateStatusMutation.mutate({ id: selectedSelection.id, status });
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">مدیریت لیست‌های انتخاب</h1>
        
        <div className="flex gap-2">
            <select 
                className="border rounded-lg px-3 py-2 bg-white"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
            >
                <option value="">همه وضعیت‌ها</option>
                <option value="draft">درحال انتخاب (Draft)</option>
                <option value="submitted">نهایی شده (Submitted)</option>
            </select>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="animate-spin text-gold-600" size={40} />
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-right">
              <thead className="bg-gray-50 text-gray-600 font-medium">
                <tr>
                  <th className="p-4">کاربر</th>
                  <th className="p-4">وضعیت</th>
                  <th className="p-4">تعداد محصول</th>
                  <th className="p-4">تاریخ ایجاد</th>
                  <th className="p-4">عملیات</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data?.data.data.map((selection) => (
                  <tr key={selection.id} className="hover:bg-gray-50 transition-colors">
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <User size={16} className="text-gray-400" />
                        <span className="font-medium text-gray-700">
                          {selection.user?.first_name} {selection.user?.last_name}
                        </span>
                        <span className="text-xs text-gray-500 mr-1">({selection.user?.phone_number})</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        selection.status === 'submitted' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {selection.status === 'submitted' ? 'نهایی شده' : 'درحال انتخاب'}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-1 text-gray-600">
                        <Package size={16} />
                        <span>{selection.items?.length || 0} مورد</span>
                      </div>
                    </td>
                    <td className="p-4 text-gray-600 font-mono text-sm">
                        <div className="flex items-center gap-2" dir="ltr">
                            <span>{format(new Date(selection.created_at), 'yyyy/MM/dd HH:mm')}</span>
                            <Calendar size={14} />
                        </div>
                    </td>
                    <td className="p-4">
                      <div className="flex gap-2">
                        <button 
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            onClick={() => handleOpenModal(selection.id)}
                            title="مشاهده جزئیات"
                        >
                            <Eye size={18} />
                        </button>
                        <button 
                            className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                            onClick={() => handleShare(selection)}
                            title="اشتراک‌گذاری"
                            disabled={sharingId === selection.id}
                        >
                            {sharingId === selection.id ? <Loader2 size={18} className="animate-spin" /> : <Share2 size={18} />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
           {data?.data.meta.total_pages > 1 && (
              <div className="p-4 border-t flex justify-center gap-2">
                  <button 
                    disabled={page === 1}
                    onClick={() => setPage(p => p - 1)}
                    className="px-3 py-1 border rounded disabled:opacity-50"
                  >
                      قبلی
                  </button>
                  <span className="px-3 py-1">صفحه {page} از {data?.data.meta.total_pages}</span>
                  <button 
                    disabled={page === data?.data.meta.total_pages}
                    onClick={() => setPage(p => p + 1)}
                    className="px-3 py-1 border rounded disabled:opacity-50"
                  >
                      بعدی
                  </button>
              </div>
           )}
        </div>
      )}

      {/* Details Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="جزئیات لیست انتخاب"
      >
        {selectedSelection && (
            <div className="space-y-6">
                {/* User Info */}
                <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="text-sm font-semibold text-gray-500 mb-2">اطلاعات کاربر</h4>
                    <div className="flex justify-between items-center">
                        <span className="font-medium">{selectedSelection.user?.first_name} {selectedSelection.user?.last_name}</span>
                        <span className="text-gray-600 font-mono">{selectedSelection.user?.phone_number}</span>
                    </div>
                </div>

                {/* Items */}
                <div>
                    <h4 className="text-sm font-semibold text-gray-500 mb-3">محصولات ({selectedSelection.items?.length || 0})</h4>
                    <div className="max-h-60 overflow-y-auto space-y-3 pr-1">
                        {selectedSelection.items?.map((item) => (
                            <div key={item.product_id} className="flex gap-3 border p-3 rounded-lg">
                                <div className="w-16 h-16 bg-gray-100 rounded overflow-hidden flex-shrink-0">
                                    {item.product?.image_urls?.[0] ? (
                                        <img src={item.product.image_urls[0]} alt="" className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="flex items-center justify-center h-full text-gray-300"><Package size={20} /></div>
                                    )}
                                </div>
                                <div>
                                    <div className="font-medium text-sm line-clamp-1">{item.product?.name || 'محصول نامشخص'}</div>
                                    <div className="text-xs text-gray-500 mt-1">کد: {item.product?.sku || item.product?.code}</div>
                                    <div className="text-xs text-gray-500 mt-1">وزن: {item.product?.weight} گرم</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Status Actions */}
                <div className="border-t pt-4">
                    <h4 className="text-sm font-semibold text-gray-500 mb-3">تغییر وضعیت</h4>
                    <div className="flex gap-2">
                        <Button
                            variant={selectedSelection.status === 'draft' ? 'primary' : 'outline'}
                            onClick={() => handleStatusChange('draft')}
                            isLoading={updateStatusMutation.isPending}
                            className="flex-1"
                            size="sm"
                        >
                            درحال انتخاب
                        </Button>
                        <Button
                            variant={selectedSelection.status === 'submitted' ? 'primary' : 'outline'}
                            onClick={() => handleStatusChange('submitted')}
                            isLoading={updateStatusMutation.isPending}
                            className="flex-1"
                            size="sm"
                        >
                            نهایی شده
                        </Button>
                    </div>
                </div>
            </div>
        )}
      </Modal>
    </div>
  );
};
